import { Switch } from '@chakra-ui/react';
import { t } from 'i18next';
import { useMemo } from 'react';
import Select from 'react-select';
import { CellProps, Column, useTable } from 'react-table';
import {
  NotificationAlertType,
  NotificationReminderType,
  NotificationStatus,
  NotificationType,
} from '~graphql/__generated__/types';
import { Columns } from './types';

const SHOWN_NOTIFICATION_TYPES = new Set<string>([
  NotificationType.Battery,
  NotificationType.BootOnSourceFailure,
  NotificationType.ConfigurationOutOfSync,
  NotificationType.FirmwareUpdate,
  NotificationType.IrRemoteUnlocked,
  NotificationType.KeypadUnlocked,
  NotificationType.NetworkDisconnected,
  NotificationType.SchedulerOutOfSync,
  NotificationType.Temperature,
  NotificationType.UsbPortUnlocked,
]);

export type FlattenedNotificationRow = {
  type: string;
  status: NotificationStatus;
  trigger: NotificationAlertType;
  frequency: NotificationReminderType;
};
type Props = {
  settings: FlattenedNotificationRow[];
  onUpdate: (settings: FlattenedNotificationRow[]) => void;
  status: NotificationStatus;
  email: boolean;
};
export function useEmailNotificationTable({ settings, onUpdate, status, email }: Props) {
  const filteredSettings = useMemo(
    () => settings.filter((s) => SHOWN_NOTIFICATION_TYPES.has(s.type)),
    [settings],
  );
  const notificationAlertLabels = useMemo(
    () => ({
      [NotificationAlertType.DoNotAlert]: t('doNotAlert'),
      [NotificationAlertType.ImmediateAndOnce]: t('immediateAndOnce'),
      [NotificationAlertType.Daily]: t('daily'),
      [NotificationAlertType.Weekly]: t('weekly'),
      [NotificationAlertType.Monthly]: t('monthly'),
    }),
    [],
  );

  const notificationTypeLabels = useMemo(
    () => ({
      [NotificationType.Battery]: t('battery'),
      [NotificationType.BootOnSourceFailure]: t('bootOnSourceFailure'),
      [NotificationType.ConfigurationOutOfSync]: t('configurationOutOfSync'),
      [NotificationType.FirmwareUpdate]: t('firmwareUpdate'),
      [NotificationType.IrRemoteUnlocked]: t('irRemoteUnlocked'),
      [NotificationType.KeypadUnlocked]: t('keypadUnlocked'),
      [NotificationType.NetworkDisconnected]: t('networkDisconnected'),
      [NotificationType.SchedulerOutOfSync]: t('schedulerOutOfSync'),
      [NotificationType.Temperature]: t('temperature'),
      [NotificationType.UsbPortUnlocked]: t('usbPortUnlocked'),
    }),
    [],
  );

  const notificationReminderLabels = useMemo(
    () => ({
      [NotificationReminderType.DoNotRemind]: t('doNotRemind'),
      [NotificationReminderType.Daily]: t('daily'),
      [NotificationReminderType.Weekly]: t('weekly'),
    }),
    [],
  );

  const notificationTriggerOptions = useMemo(
    () =>
      Object.entries(notificationAlertLabels).map(([key, label]) => ({
        value: key,
        label,
      })),
    [notificationAlertLabels],
  );

  const notificationFrequencyOptions = useMemo(
    () =>
      Object.entries(notificationReminderLabels).map(([key, label]) => ({
        value: key,
        label,
      })),
    [notificationReminderLabels],
  );
  const columns = useMemo<Array<Column<FlattenedNotificationRow>>>(
    () => [
      {
        id: Columns.Type,
        Header: t('type'),
        accessor: 'type',
        width: '100px',
        Cell: ({ value, row }) => (
          <span
            style={{
              color:
                status !== NotificationStatus.Enabled ||
                !email ||
                row.original.status === NotificationStatus.Disabled
                  ? '#A0A0A0'
                  : 'inherit',
            }}
          >
            {(notificationTypeLabels as Partial<Record<NotificationType, string>>)[
              value as NotificationType
            ] || value}
          </span>
        ),
        minWidth: 100,
        maxWidth: 100,
      },
      {
        id: Columns.Status,
        Header: t('status'),
        accessor: 'status',
        Cell: ({ value, row, data }: CellProps<FlattenedNotificationRow, NotificationStatus>) => {
          const rowDisabled = status !== NotificationStatus.Enabled || !email;
          return (
            <Switch
              isChecked={value === NotificationStatus.Enabled}
              isDisabled={rowDisabled}
              colorScheme={rowDisabled ? 'gray' : 'blue'}
              onChange={(e) => {
                const newStatus = e.target.checked
                  ? NotificationStatus.Enabled
                  : NotificationStatus.Disabled;
                const updatedSettings = (data as FlattenedNotificationRow[]).map(
                  (r: FlattenedNotificationRow) =>
                    r.type === row.original.type ? { ...r, status: newStatus } : r,
                );
                onUpdate(updatedSettings);
              }}
            />
          );
        },
        width: '60px',
        minWidth: 60,
        maxWidth: 60,
      },
      {
        id: Columns.Trigger,
        Header: t('alert'),
        accessor: 'trigger',
        Cell: ({
          value,
          row,
          data,
        }: CellProps<FlattenedNotificationRow, NotificationAlertType>) => {
          const isDisabled =
            status !== NotificationStatus.Enabled ||
            !email ||
            row.original.status === NotificationStatus.Disabled;

          const isImmediateOrDoNotAlertOnly =
            row.original.type === NotificationType.Temperature ||
            row.original.type === NotificationType.Battery;
          const triggerOptions = isImmediateOrDoNotAlertOnly
            ? notificationTriggerOptions.filter(
                (o) =>
                  o.value === NotificationAlertType.ImmediateAndOnce ||
                  o.value === NotificationAlertType.DoNotAlert,
              )
            : notificationTriggerOptions;

          const selectedOption = triggerOptions.find((option) => option.value === value);
          return (
            <Select
              value={selectedOption}
              isDisabled={isDisabled}
              isMulti={false}
              escapeClearsValue={false}
              isClearable={false}
              isSearchable={false}
              onChange={(selectedOption) => {
                if (!selectedOption) return;
                const newTrigger = selectedOption.value as NotificationAlertType;
                const updatedSettings = (data as FlattenedNotificationRow[]).map(
                  (r: FlattenedNotificationRow) =>
                    r.type === row.original.type ? { ...r, trigger: newTrigger } : r,
                );
                onUpdate(updatedSettings);
              }}
              options={triggerOptions}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: '200px',
                }),
                indicatorSeparator: () => ({
                  display: 'none',
                }),
              }}
            />
          );
        },
        width: '130px',
        minWidth: 130,
        maxWidth: 130,
      },
      {
        id: Columns.Frequency,
        Header: t('reminder'),
        accessor: 'frequency',
        Cell: ({
          value,
          row,
          data,
        }: CellProps<FlattenedNotificationRow, NotificationReminderType>) => {
          const isDisabled =
            status !== NotificationStatus.Enabled ||
            !email ||
            row.original.status === NotificationStatus.Disabled;

          const isBattery = row.original.type === NotificationType.Battery;
          const frequencyOptions = isBattery
            ? notificationFrequencyOptions.filter(
                (o) =>
                  o.value === NotificationReminderType.Daily ||
                  o.value === NotificationReminderType.DoNotRemind,
              )
            : notificationFrequencyOptions;

          const selectedOption = frequencyOptions.find((option) => option.value === value);

          return (
            <Select
              value={selectedOption}
              isDisabled={isDisabled}
              isMulti={false}
              escapeClearsValue={false}
              isClearable={false}
              isSearchable={false}
              onChange={(selectedOption) => {
                if (!selectedOption) return;

                const newFrequency = selectedOption.value as NotificationReminderType;

                const updatedSettings = (data as FlattenedNotificationRow[]).map(
                  (r: FlattenedNotificationRow) =>
                    r.type === row.original.type ? { ...r, frequency: newFrequency } : r,
                );

                onUpdate(updatedSettings);
              }}
              options={frequencyOptions}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: '200px',
                }),
                indicatorSeparator: () => ({
                  display: 'none',
                }),
              }}
            />
          );
        },
        width: '130px',
        minWidth: 130,
        maxWidth: 130,
      },
    ],
    [
      email,
      notificationFrequencyOptions,
      notificationTriggerOptions,
      notificationTypeLabels,
      onUpdate,
      status,
    ],
  );

  return useTable({
    columns,
    data: filteredSettings,
  });
}
